import { apiClient } from '@/infrastructure/api'
import {
  PbNewReturnReason,
  PbPatchBrandingReq,
  PbPatchReturnProcessConditionsReq,
  PbReturnAmountRule,
  PbSendTestWebhookRequestWebhookEnum,
  PbUploadBrandingImageReq,
  SettingsApi,
  SettingsApiOnboardRequest
} from '@itsrever/rever-api'

const settingsApi = new SettingsApi(undefined, undefined, apiClient)

export async function getWithdrawalSettings(ecommerceId: string) {
  const { data } = await settingsApi.findWithdrawalSettings({ ecommerceId })
  return data
}

export async function setWithdrawalSettings(
  ecommerceId: string,
  enabled: boolean
) {
  const { data } = await settingsApi.setWithdrawalSettings({
    ecommerceId,
    pbSetWithdrawalSettingsRequest: { enabled }
  })
  return data
}

export async function onboardEcommerce(request: SettingsApiOnboardRequest) {
  const response = await settingsApi.onboard(request)
  return response.data
}

export async function getShippingConfiguration(ecommerceId: string) {
  const response = await settingsApi.getReturnAmount({ ecommerceId })
  return response.data
}

export async function updateShippingConfiguration(
  ecommerceId: string,
  rules: PbReturnAmountRule[]
) {
  const response = await settingsApi.postReturnAmount({
    ecommerceId,
    pbPostReturnAmountRequest: {
      ecommerce_id: ecommerceId,
      rules
    }
  })
  return response.data
}

export async function getPublicAvailableToConfigure(ecommerceId: string) {
  const response = await settingsApi.publicReturnAmountAvailable({
    ecommerceId
  })
  return response.data
}

export async function getAvailableToConfigure(ecommerceId: string) {
  const response = await settingsApi.getReturnAmountAvailable({ ecommerceId })
  return response.data
}

export async function getAllReturnReasons(ecommerceId: string) {
  const response = await settingsApi.getAllReturnReasons({ ecommerceId })
  return response.data
}

export async function saveReturnReasons(
  ecommerce_id: string,
  return_reasons: PbNewReturnReason[]
) {
  const response = await settingsApi.saveReturnReasons({
    pbSaveReturnReasonsReq: { ecommerce_id, return_reasons }
  })
  return response.data
}

export async function getReturnConditions(ecommerceId: string) {
  const response = await settingsApi.findReturnProcessConditions({
    ecommerceId
  })
  return response.data
}

// Thin pass-through on purpose: the request object is the PATCH body, and the
// endpoint only writes the fields it receives.
export async function patchReturnConditions(
  request: PbPatchReturnProcessConditionsReq
) {
  const response = await settingsApi.patchReturnProcessConditions({
    pbPatchReturnProcessConditionsReq: request
  })
  return response.data
}

export async function getBranding(ecommerceId: string) {
  const response = await settingsApi.findBranding({ ecommerceId })
  return response.data
}

// Same partial-update contract as the return conditions patch: the body is what gets written,
// and the attributes an e-commerce may not change are refused by the endpoint.
export async function patchBranding(request: PbPatchBrandingReq) {
  const response = await settingsApi.patchBranding({
    pbPatchBrandingReq: request
  })
  return response.data
}

// Stores the image and points the branding at it in one call, so this both uploads and saves.
// The three image urls are refused by patchBranding for that reason: uploading is the only way an
// e-commerce sets them, which keeps the portal on images REVER serves.
export async function uploadBrandingImage(request: PbUploadBrandingImageReq) {
  const response = await settingsApi.uploadBrandingImage({
    pbUploadBrandingImageReq: request
  })
  return response.data
}

export async function getWebhookSigningSecret(ecommerceId: string) {
  const response = await settingsApi.getWebhookSigningSecret({ ecommerceId })
  return response.data
}

export async function listWebhookSigningSecrets(ecommerceId: string) {
  const response = await settingsApi.listWebhookSigningSecrets({ ecommerceId })
  return response.data
}

export async function generateWebhookSigningSecret(ecommerceId: string) {
  const response = await settingsApi.generateWebhookSigningSecret({
    ecommerceId,
    pbWebhookSigningSecretRequest: { ecommerce_id: ecommerceId }
  })
  return response.data
}

export async function revokeWebhookSigningSecret(ecommerceId: string) {
  const response = await settingsApi.revokeWebhookSigningSecret({ ecommerceId })
  return response.data
}

export async function getWebhookSubscriptions(ecommerceId: string) {
  const response = await settingsApi.getWebhookSubscriptions({ ecommerceId })
  return response.data
}

export async function upsertWebhookSubscriptions(
  ecommerceId: string,
  subscriptions: {
    onStartEnabled: boolean
    onCompletedEnabled: boolean
    url: string
  }
) {
  const response = await settingsApi.upsertWebhookSubscriptions({
    ecommerceId,
    pbUpsertWebhookSubscriptionsRequest: {
      ecommerce_id: ecommerceId,
      on_start_enabled: subscriptions.onStartEnabled,
      on_completed_enabled: subscriptions.onCompletedEnabled,
      url: subscriptions.url
    }
  })
  return response.data
}

export async function sendTestWebhook(
  ecommerceId: string,
  webhook: PbSendTestWebhookRequestWebhookEnum,
  url: string
) {
  const response = await settingsApi.sendTestWebhook({
    ecommerceId,
    pbSendTestWebhookRequest: { ecommerce_id: ecommerceId, webhook, url }
  })
  return response.data
}
// API keys. The value-bearing calls are never cached anywhere: the server sends no-store and
// nothing here keeps the plaintext beyond the caller that asked for it.
export async function listApiKeys(ecommerceGroups?: string[]) {
  const response = await settingsApi.listAPIKeys({ ecommerceGroups })
  return response.data
}

export async function createApiKey(
  ecommerceGroup: string,
  name: string,
  scopes: string[]
) {
  const response = await settingsApi.createAPIKey({
    pbCreateAPIKeyRequest: { ecommerce_group: ecommerceGroup, name, scopes }
  })
  return response.data
}

export async function getApiKeyValue(ecommerceGroup: string, name: string) {
  const response = await settingsApi.getAPIKeyValue({ ecommerceGroup, name })
  return response.data
}

export async function revokeApiKey(ecommerceGroup: string, name: string) {
  const response = await settingsApi.revokeAPIKey({ ecommerceGroup, name })
  return response.data
}
